// Named export: see the note at the top of LibraryFolder.js — the default
// export of src/store is the store FACTORY.
import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { deepFreeze, timestampToDate } from "../util/misc";

// Known action strings written by the library store actions.
export const LIBRARY_ACTIVITY_ACTIONS = Object.freeze({
  FOLDER_CREATE: "folder.create",
  FOLDER_EDIT: "folder.edit",
  FOLDER_MOVE: "folder.move",
  FOLDER_DELETE: "folder.delete",
  FILE_UPLOAD: "file.upload",
  FILE_RENAME: "file.rename",
  FILE_MOVE: "file.move",
  FILE_DELETE: "file.delete",
  FILE_RESTORE: "file.restore",
  FILE_PURGE: "file.purge",
});

// Sentence-case labels, translated at render time.
export const LIBRARY_ACTIVITY_ACTION_LABELS = Object.freeze({
  "folder.create": "Created folder",
  "folder.edit": "Edited folder",
  "folder.move": "Moved folder",
  "folder.delete": "Deleted folder",
  "file.upload": "Uploaded file",
  "file.rename": "Renamed file",
  "file.move": "Moved file",
  "file.delete": "Moved to Trash",
  "file.restore": "Restored file",
  "file.purge": "Permanently deleted",
});

export const LIBRARY_ACTIVITY_ACTION_ICONS = Object.freeze({
  "folder.create": "folder",
  "folder.edit": "edit",
  "folder.move": "relocate",
  "folder.delete": "delete",
  "file.upload": "upload",
  "file.rename": "edit",
  "file.move": "relocate",
  "file.delete": "delete",
  "file.restore": "restore",
  "file.purge": "delete",
});

export const LIBRARY_ACTIVITY_FIELDS = {
  at: null,
  actorID: null,
  action: "",
  subjectType: "", // "file" | "folder"
  subjectID: null,
  subjectName: "",
  tier: "public",
  // Freeform context about what changed (e.g. { from, to, folderID, size }).
  details: null,
};
deepFreeze(LIBRARY_ACTIVITY_FIELDS);

export default class LibraryActivity extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, LIBRARY_ACTIVITY_FIELDS);
    data.at = timestampToDate(data.at);
    super(data);
    this.id = id || null;
  }

  get path() {
    return LibraryActivity.getPath(this.id);
  }

  static getPath(id) {
    let path = "libraryActivity";
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(entry) {
    return entry.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new LibraryActivity(snapshot.id, data);
  }

  get actor() {
    return this.data.actorID
      ? store.state.main.users[this.data.actorID] || null
      : null;
  }

  get actionLabel() {
    return LIBRARY_ACTIVITY_ACTION_LABELS[this.data.action] || this.data.action;
  }

  get actionIcon() {
    return LIBRARY_ACTIVITY_ACTION_ICONS[this.data.action] || "recent";
  }

  // "old name → new name" for renames; nothing for the rest.
  get detailsText() {
    const details = this.data.details || {};
    if (this.data.action === "file.rename" && details.from && details.to) {
      return `${details.from} → ${details.to}`;
    }
    return "";
  }
}
